import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import propertyLiabilityService from "../services/propertyLiabilityService";
import Loader from "../components/Loader";
import styles from "../assets/propertyLiabilityEdit.module.css";

const PropertyLiabilityEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [property, setProperty] = useState(null);
  const [propertyLiabilities, setPropertyLiabilities] = useState({});
  const [liabilityTypes, setLiabilityTypes] = useState([]);
  const [liabilityOptions, setLiabilityOptions] = useState({});

  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Mock data for fallback - EXACT Laravel match
  const mockLiabilityTypes = [
    { id: 1, name: "24/7 SIA Security", order: 1 },
    { id: 2, name: "Asbestos management", order: 2 },
    { id: 3, name: "Bins", order: 3 },
    { id: 4, name: "Caretaker", order: 4 },
    { id: 5, name: "Council tax", order: 5 },
    { id: 6, name: "Electric bills", order: 6 },
    { id: 7, name: "Emergency Lighting Servicing", order: 7 },
    { id: 8, name: "Emergency repairs", order: 8 },
    { id: 9, name: "External repairs", order: 9 },
    { id: 10, name: "Fire alarm servicing", order: 10 },
    { id: 11, name: "Fire Extinguisher Servicing", order: 11 },
    { id: 12, name: "Fire Risk Assessment", order: 12 },
    { id: 13, name: "Gas bills", order: 13 },
    { id: 14, name: "Gas Safe certificate", order: 14 },
    { id: 15, name: "Initial Fit out and Clearance", order: 15 },
    { id: 16, name: "Internal repairs", order: 16 },
    { id: 17, name: "Legionella testing", order: 17 },
    { id: 18, name: "NIC certificate", order: 18 },
    { id: 19, name: "Revenue Share", order: 19 },
    { id: 20, name: "Water bills", order: 20 },
    { id: 21, name: "Wireless smoke alarms", order: 21 },
  ];

  const mockLiabilityOptions = {
    1: "Please Select",
    2: "Client",
    3: "Client/GGM",
    4: "GGM",
    5: "Guardians",
    6: "N/A",
  };

  const mockProperty = {
    id: parseInt(id),
    name: `Property ${id}`,
    address: `123 Test Street, Test City, TE1 1ST`,
  };

  const mockPropertyLiabilities = {
    1: { work: 6, cost: 6, work_name: "N/A", cost_name: "N/A" },
    2: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    3: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    4: { work: 6, cost: 6, work_name: "N/A", cost_name: "N/A" },
    5: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    6: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    7: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    8: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    9: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    10: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    11: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    12: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    13: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    14: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    15: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    16: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    17: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    18: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    19: { work: 6, cost: 6, work_name: "N/A", cost_name: "N/A" },
    20: { work: 2, cost: 2, work_name: "Client", cost_name: "Client" },
    21: { work: 6, cost: 6, work_name: "N/A", cost_name: "N/A" },
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to load from API first
      try {
        const response = await propertyLiabilityService.getEditData(id);

        setProperty(response.property || mockProperty);
        setPropertyLiabilities(response.propertyLiabilities || {});
        setLiabilityTypes(response.liabilityTypes || mockLiabilityTypes);
        setLiabilityOptions(response.liabilityOptions || mockLiabilityOptions);

        // Initialize form data with existing values for ALL liability types
        const initialFormData = {};
        const allLiabilityTypes = response.liabilityTypes || mockLiabilityTypes;
        
        // Initialize all liability types with empty values first
        allLiabilityTypes.forEach((type) => {
          initialFormData[`liability[${type.id}][work]`] = "";
          initialFormData[`liability[${type.id}][cost]`] = "";
        });
        
        // Then populate with existing values if they exist
        if (response.propertyLiabilities) {
          Object.keys(response.propertyLiabilities).forEach((typeId) => {
            const liability = response.propertyLiabilities[typeId];
            initialFormData[`liability[${typeId}][work]`] =
              liability.work || "";
            initialFormData[`liability[${typeId}][cost]`] =
              liability.cost || "";
          });
        }
        setFormData(initialFormData);
      } catch (apiError) {
        console.warn("API failed, using mock data:", apiError);

        // Fallback to mock data
        setProperty(mockProperty);
        setPropertyLiabilities(mockPropertyLiabilities);
        setLiabilityTypes(mockLiabilityTypes);
        setLiabilityOptions(mockLiabilityOptions);

        // Initialize form data with mock values for ALL liability types
        const initialFormData = {};
        
        // Initialize all liability types with empty values first
        mockLiabilityTypes.forEach((type) => {
          initialFormData[`liability[${type.id}][work]`] = "";
          initialFormData[`liability[${type.id}][cost]`] = "";
        });
        
        // Then populate with existing mock values if they exist
        Object.keys(mockPropertyLiabilities).forEach((typeId) => {
          const liability = mockPropertyLiabilities[typeId];
          initialFormData[`liability[${typeId}][work]`] = liability.work || "";
          initialFormData[`liability[${typeId}][cost]`] = liability.cost || "";
        });
        setFormData(initialFormData);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setError("Failed to load property liabilities data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }

    // Also clear validation error for the paired field if both are now empty
    const typeId = field.match(/liability\[(\d+)\]/)?.[1];
    if (typeId) {
      const isWorkField = field.includes('[work]');
      const isCostField = field.includes('[cost]');
      
      if (isWorkField) {
        const costField = `liability[${typeId}][cost]`;
        const costValue = formData[costField];
        if (!value && !costValue && validationErrors[costField]) {
          setValidationErrors((prev) => ({
            ...prev,
            [costField]: null,
          }));
        }
      } else if (isCostField) {
        const workField = `liability[${typeId}][work]`;
        const workValue = formData[workField];
        if (!value && !workValue && validationErrors[workField]) {
          setValidationErrors((prev) => ({
            ...prev,
            [workField]: null,
          }));
        }
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    liabilityTypes.forEach((type) => {
      const workField = `liability[${type.id}][work]`;
      const costField = `liability[${type.id}][cost]`;
      const workValue = formData[workField];
      const costValue = formData[costField];

      // Only validate if at least one field has a value (Laravel behavior)
      if (workValue || costValue) {
        if (!workValue) {
          errors[workField] = "Work option is required";
        }

        if (!costValue) {
          errors[costField] = "Cost option is required";
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Prepare data in the format expected by Laravel
      const submitData = {
        property_id: parseInt(id),
        liability: {},
      };

      liabilityTypes.forEach((type) => {
        const workValue = formData[`liability[${type.id}][work]`];
        const costValue = formData[`liability[${type.id}][cost]`];

        if (workValue || costValue) {
          submitData.liability[type.id] = {
            work: workValue || null,
            cost: costValue || null,
          };
        }
      });

      // Try to save via API
      try {
        await propertyLiabilityService.update(id, submitData);
        setSuccess("Property Liabilities successfully updated");
        console.log("✅ Property liabilities updated successfully");

        // Redirect to property show page after a short delay
        setTimeout(() => {
          navigate(`/properties/${id}`);
        }, 1500);
      } catch (apiError) {
        console.error("❌ [FRONTEND] API save failed:", apiError);
        
        // Laravel-style error handling based on HTTP status codes
        if (apiError.response) {
          const { status, data } = apiError.response;
          
          switch (status) {
            case 401:
              setError("Authentication required. Please log in again.");
              break;
            case 403:
              setError("You do not have permission to update property liabilities.");
              break;
            case 422:
              if (data.errors) {
                const errorMessages = Object.values(data.errors).flat();
                setError(`Validation failed: ${errorMessages.join(', ')}`);
              } else {
                setError(data.message || "Validation failed");
              }
              break;
            case 404:
              setError("Property not found.");
              break;
            case 500:
              setError("Server error occurred. Please try again later.");
              break;
            default:
              setError(data.message || "An unexpected error occurred");
          }
        } else {
          // Network error or other issues
          setError("Network error. Please check your connection and try again.");
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      setError("Failed to save property liabilities");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles['property-liability-edit']}>
        <div className={styles.container}>
          <div className={styles['loading-container']}>
            <Loader />
            <p>Loading property liabilities...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['property-liability-edit']}>
      <div className={styles.container}>
        {/* Breadcrumbs - Laravel equivalent: Breadcrumbs::render('properties.liabilities.edit', $property) */}
        <div className="breadcrumb-row">
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: "pointer" }}
            onClick={() => navigate('/dashboard')}
          >
            Dashboard
          </span>
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: "pointer" }}
            onClick={() => navigate('/properties')}
          >
            / Properties
          </span>
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: "pointer" }}
            onClick={() => navigate(`/properties/${id}`)}
          >
            / {property?.name || `Property ${id}`}
          </span>
          <div
            style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
          >
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-properties">Edit Liabilities</span>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className={`${styles.alert} ${styles['alert-danger']}`} role="alert">
            <i className={styles['alert-icon']}>⚠</i>
            {error}
          </div>
        )}

        {success && (
          <div className={`${styles.alert} ${styles['alert-success']}`} role="alert">
            <i className={styles['alert-icon']}>✓</i>
            {success}
          </div>
        )}

        {/* Main Content */}
        <div className={styles['content-wrapper']}>
          <div className={`${styles.panel} ${styles['panel-default']} ${styles['panel-form']}`}>
            <div className={styles['panel-heading']}>
              <div className={styles['panel-title']}>
                <h3>Edit Liabilities</h3>
              </div>
            </div>

            <div className={styles['panel-body']}>
              <form onSubmit={handleSubmit} className={styles['liability-form']}>
                <input type="hidden" name="property_id" value={id} />

                {/* Form Header */}
                <div className={styles['form-header']}>
                  <div className={styles['form-header-row']}>
                    <div className={`${styles['form-header-cell']} ${styles['liability-type-header']}`}>
                      <span>Liability Type</span>
                    </div>
                    <div className={`${styles['form-header-cell']} ${styles['work-header']}`}>
                      <span>Work Responsibility</span>
                    </div>
                    <div className={`${styles['form-header-cell']} ${styles['cost-header']}`}>
                      <span>Cost Responsibility</span>
                    </div>
                  </div>
                </div>

                {/* Liability Fields */}
                <div className={styles['form-fields']}>
                  {liabilityTypes.map((type, index) => (
                    <div
                      key={type.id}
                      className={`${styles['form-row']} ${index % 2 === 0 ? styles.even : styles.odd}`}
                    >
                      <div className={`${styles['form-cell']} ${styles['liability-type-cell']}`}>
                        <div className={styles['liability-type-name']}>{type.name}</div>
                      </div>

                      <div className={`${styles['form-cell']} ${styles['work-cell']}`}>
                        <div className={styles['form-group']}>
                          <select
                            name={`liability[${type.id}][work]`}
                            className={`${styles['form-control']} ${
                              validationErrors[`liability[${type.id}][work]`]
                                ? styles['has-error']
                                : ""
                            }`}
                            value={
                              formData[`liability[${type.id}][work]`] || ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                `liability[${type.id}][work]`,
                                e.target.value
                              )
                            }
                            required
                          >
                            {Object.entries(liabilityOptions).map(
                              ([optionId, optionName]) => (
                                <option key={optionId} value={optionId}>
                                  {optionName}
                                </option>
                              )
                            )}
                          </select>
                          {validationErrors[`liability[${type.id}][work]`] && (
                            <div className={styles['error-message']}>
                              {validationErrors[`liability[${type.id}][work]`]}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={`${styles['form-cell']} ${styles['cost-cell']}`}>
                        <div className={styles['form-group']}>
                          <select
                            name={`liability[${type.id}][cost]`}
                            className={`${styles['form-control']} ${
                              validationErrors[`liability[${type.id}][cost]`]
                                ? styles['has-error']
                                : ""
                            }`}
                            value={
                              formData[`liability[${type.id}][cost]`] || ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                `liability[${type.id}][cost]`,
                                e.target.value
                              )
                            }
                            required
                          >
                            {Object.entries(liabilityOptions).map(
                              ([optionId, optionName]) => (
                                <option key={optionId} value={optionId}>
                                  {optionName}
                                </option>
                              )
                            )}
                          </select>
                          {validationErrors[`liability[${type.id}][cost]`] && (
                            <div className={styles['error-message']}>
                              {validationErrors[`liability[${type.id}][cost]`]}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Form Actions */}
                <div
                  className={styles['form-actions']}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div className={styles['action-buttons']}>
                    <button
                      style={{
                        backgroundColor: "#1B2046",
                        border: "none",
                        height: "32px",
                      }}
                      type="submit"
                      className={`${styles.btn} ${styles['btn-primary']}`}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className={styles.spinner}></span>
                          Saving...
                        </>
                      ) : (
                        "Save Property Liabilities"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyLiabilityEdit;
